import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

const disableHeaderMenuHover = () => {
  document.querySelectorAll('header-menu .menu-list__list-item[on\\:pointerenter]').forEach((item) => {
    item.removeAttribute('on:pointerenter');
  });
};

const installHeaderHoverStyles = () => {
  if (document.getElementById('header-hover-fix')) return;

  const style = document.createElement('style');
  style.id = 'header-hover-fix';
  style.textContent = `
    @media (hover: hover) {
      #header-component[transparent]:not([data-sticky-state='active']):not(:has(header-menu[aria-expanded='true'])) {
        --closed-underlay-height: 0px !important;
      }
    }

    #header-component[transparent] header-menu .menu-list__link,
    #header-component[transparent] header-menu .menu-list__link:hover,
    #header-component[transparent] header-menu .menu-list__link:focus,
    #header-component[transparent] header-menu .menu-list__link:focus-visible,
    #header-component[transparent] header-menu .menu-list__list-item:hover .menu-list__link,
    #header-component[transparent] header-menu .menu-list__list-item:focus-within .menu-list__link,
    #header-component[transparent] header-menu[aria-expanded='true'] .menu-list__link {
      --menu-top-level-font-color: #ffffff !important;
      --menu-top-level-font-color-rgb: 255 255 255 !important;
      color: #ffffff !important;
    }

    #header-component[transparent] .header-actions__action,
    #header-component[transparent] .account-button,
    #header-component[transparent] .account-button__icon,
    #header-component[transparent] .account-button__text,
    #header-component[transparent] search-button,
    #header-component[transparent] cart-icon {
      --button-color: #ffffff !important;
      --color-account-icon: #ffffff !important;
      color: #ffffff !important;
    }

    #header-component[transparent] .header-actions__action svg,
    #header-component[transparent] .account-button__icon {
      color: #ffffff !important;
    }
  `;
  document.head.append(style);
};

disableHeaderMenuHover();
installHeaderHoverStyles();

const headerMenuHoverObserver = new MutationObserver(disableHeaderMenuHover);
headerMenuHoverObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['on:pointerenter'],
});
